import apiService from './api.service';

export interface ErrorGroup {
  id: string;
  fingerprint: string;
  error_type: string;
  message: string;
  culprit?: string;
  first_seen_at: string;
  last_seen_at: string;
  occurrence_count: number;
  user_count: number;
  status: 'unresolved' | 'resolved' | 'ignored' | 'in_progress';
  priority?: 'critical' | 'high' | 'medium' | 'low';
  assigned_to?: string;
}

export interface ErrorStats {
  totalErrors: number;
  errorGroups: number;
  affectedUsers: number;
  errorRate: number;
  newErrors: number;
  regressions: number;
  resolvedErrors: number;
}

export interface Release {
  id: string;
  version: string;
  environment: string;
  deployed_at: string;
  error_count: number;
  new_error_count: number;
  regressed_error_count: number;
}

export interface SourceMap {
  id: string;
  file_name: string;
  release_version: string;
  uploaded_at: string;
  size_bytes: number;
  s3_key: string;
}

class ErrorTrackingService {
  private baseUrl = '/v1/errors';

  /**
   * Get error overview statistics
   */
  async getOverviewStats(
    applicationId: string,
    startTime?: string,
    endTime?: string
  ): Promise<{
    stats: ErrorStats;
    topErrors: ErrorGroup[];
  }> {
    const params = new URLSearchParams({ application_id: applicationId });
    if (startTime) params.append('start_time', startTime);
    if (endTime) params.append('end_time', endTime);

    const response = await apiService.get(`${this.baseUrl}/overview?${params.toString()}`);
    return response;
  }

  /**
   * Get error groups with filters
   */
  async getErrorGroups(
    applicationId: string,
    startTime?: string,
    endTime?: string,
    filters?: {
      status?: string;
      priority?: string;
      errorType?: string;
      environment?: string;
      release?: string;
      search?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<{
    errorGroups: ErrorGroup[];
    total: number;
  }> {
    const params = new URLSearchParams({ application_id: applicationId });
    if (startTime) params.append('start_time', startTime);
    if (endTime) params.append('end_time', endTime);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.priority) params.append('priority', filters.priority);
    if (filters?.errorType) params.append('error_type', filters.errorType);
    if (filters?.environment) params.append('environment', filters.environment);
    if (filters?.release) params.append('release', filters.release);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.limit) params.append('limit', filters.limit.toString());
    if (filters?.offset) params.append('offset', filters.offset.toString());

    const response = await apiService.get(`${this.baseUrl}/groups?${params.toString()}`);
    return response;
  }

  /**
   * Get error group details
   */
  async getErrorGroup(groupId: string): Promise<ErrorGroup> {
    const response = await apiService.get(`${this.baseUrl}/groups/${groupId}`);
    return response.data || response;
  }

  /**
   * Update error group status
   */
  async updateErrorGroupStatus(
    groupId: string,
    status: 'resolved' | 'ignored' | 'in_progress'
  ): Promise<void> {
    await apiService.put(`${this.baseUrl}/groups/${groupId}/status`, { status });
  }

  /**
   * Get releases
   */
  async getReleases(applicationId: string): Promise<Release[]> {
    const params = new URLSearchParams({ application_id: applicationId });
    const response = await apiService.get(`${this.baseUrl}/releases?${params.toString()}`);
    return response.data || response;
  }

  /**
   * Create release
   */
  async createRelease(data: {
    applicationId: string;
    version: string;
    environment: string;
    deployed_at?: string;
  }): Promise<Release> {
    const response = await apiService.post(`${this.baseUrl}/releases`, data);
    return response.data || response;
  }

  /**
   * Get source maps
   */
  async getSourceMaps(applicationId: string, releaseVersion?: string): Promise<SourceMap[]> {
    const params = new URLSearchParams({ application_id: applicationId });
    if (releaseVersion) params.append('release_version', releaseVersion);

    const response = await apiService.get(`${this.baseUrl}/source-maps?${params.toString()}`);
    return response.data || response;
  }

  /**
   * Upload source map
   */
  async uploadSourceMap(
    applicationId: string,
    releaseVersion: string,
    file: File
  ): Promise<SourceMap> {
    const formData = new FormData();
    formData.append('application_id', applicationId);
    formData.append('release_version', releaseVersion);
    formData.append('file', file);

    const response = await apiService.post(`${this.baseUrl}/source-maps`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data || response;
  }

  /**
   * Delete source map
   */
  async deleteSourceMap(sourceMapId: string): Promise<void> {
    await apiService.delete(`${this.baseUrl}/source-maps/${sourceMapId}`);
  }
}

const errorTrackingService = new ErrorTrackingService();
export default errorTrackingService;
